/**
 * Strategic Intelligence Dashboard Helpers (DC-1367)
 *
 * Shared helper functions extracted from StrategicIntelligenceDashboard.tsx.
 */

import type { TechnicalAssetScore, ExecutionRiskData, BridgeIntelligence } from './types';
import { hasValue, type Quantified } from '../../types/actionPlanValueProvenance';

// Cream/teal tokens (v2.0 fold). Excellent/Good deliberately collapse onto
// --tri-good (the green/blue split has no cream equivalent — the rating label
// carries the distinction), matching the ALCOA getScoreColor convention.
export function getScoreColor(score: number): string {
  if (score >= 60) return 'var(--tri-good)';
  if (score >= 40) return 'var(--tri-soft)';
  if (score >= 20) return 'var(--tri-terra)';
  return 'var(--tri-hard)';
}

export function getRiskColor(level: string): string {
  switch (level) {
    case 'LOW': return 'var(--tri-good)';
    case 'MODERATE': return 'var(--tri-soft)';
    case 'HIGH': return 'var(--tri-terra)';
    case 'CRITICAL': return 'var(--tri-hard)';
    default: return 'var(--tri-slate)';
  }
}

export function getRiskLabel(level: string): string {
  switch (level) {
    case 'LOW': return 'Low Risk';
    case 'MODERATE': return 'Moderate';
    case 'HIGH': return 'High Risk';
    case 'CRITICAL': return 'Critical';
    case 'NOT_CONFIGURED': return 'Not Configured';
    default: return level;
  }
}

/**
 * Derive ROI projections from Action Plan tasks.
 * Uses the same data source as IntelligencePrism for consistency.
 */

export function deriveROIProjections(
  actionPlanTasks: Array<{
    value_calculation?: { pistoia_driver?: string };
    driver?: string;
    /** Provenance-bearing on the wire (S4d); an unpriced task is not a zero one. */
    value_usd?: Quantified<number>;
  }>
): BridgeIntelligence['roi_projections'] {
  if (!actionPlanTasks || actionPlanTasks.length === 0) {
    return undefined;
  }

  const driverMap: Record<string, { name: string; value: number; tasks: number }> = {};

  actionPlanTasks.forEach((task) => {
    const driverName = task.value_calculation?.pistoia_driver || task.driver || 'General';
    // `|| 0` on a Quantified object yielded NaN here too.
    const taskValue = hasValue(task.value_usd) ? task.value_usd.value : 0;

    if (!driverMap[driverName]) {
      driverMap[driverName] = { name: driverName, value: 0, tasks: 0 };
    }

    driverMap[driverName].value += taskValue;
    driverMap[driverName].tasks++;
  });

  const drivers = Object.values(driverMap)
    .filter(d => d.value > 0)
    .sort((a, b) => b.value - a.value);
  const total_value = drivers.reduce((sum, d) => sum + d.value, 0);

  return { total_value, drivers };
}

export function generateScoreImprovementGuidance(
  score: TechnicalAssetScore | null
): { target: number; targetRating: string; weakestPillar: string; summary: string } {
  if (!score) {
    return {
      target: 50,
      targetRating: 'Fair',
      weakestPillar: '',
      summary: 'Complete station analyses to receive improvement guidance.',
    };
  }

  let target: number;
  let targetRating: string;
  if (score.overall_score < 30) {
    target = 50;
    targetRating = 'Fair';
  } else if (score.overall_score < 50) {
    target = 50;
    targetRating = 'Fair';
  } else if (score.overall_score < 70) {
    target = 70;
    targetRating = 'Good';
  } else if (score.overall_score < 85) {
    target = 85;
    targetRating = 'Excellent';
  } else {
    target = 100;
    targetRating = 'Excellent';
  }

  const pillars = [
    { key: 'statistical_health', label: 'Statistical Health', score: score.pillars.statistical_health?.score ?? 0 },
    { key: 'semantic_maturity', label: 'Semantic Clarity', score: score.pillars.semantic_maturity?.score ?? 0 },
    { key: 'governance_fair', label: 'Governance & Compliance', score: score.pillars.governance_fair?.score ?? 0 },
  ];
  const weakest = pillars.sort((a, b) => a.score - b.score)[0];

  let summary: string;
  if (weakest.key === 'statistical_health') {
    summary = `Focus on data quality and ML readiness metrics. ${weakest.label} at ${Math.round(weakest.score)}% is limiting your score.`;
  } else if (weakest.key === 'semantic_maturity') {
    summary = `Increase ontology coverage - ${weakest.label} at ${Math.round(weakest.score)}% is your weakest area.`;
  } else {
    summary = `Address FAIR compliance gaps - ${weakest.label} at ${Math.round(weakest.score)}% needs attention.`;
  }

  return { target, targetRating, weakestPillar: weakest.label, summary };
}

export function generateExecutionRiskGuidance(
  risk: ExecutionRiskData | null
): { primaryBlocker: string; summary: string } {
  if (!risk) {
    return {
      primaryBlocker: 'unknown',
      summary: 'Configure organizational settings for risk assessment.',
    };
  }

  const factors = [
    { key: 'people', score: risk.people_readiness_score, source: risk.interview_count },
    { key: 'governance', score: risk.governance_maturity_score, source: risk.document_count },
    { key: 'interop', score: risk.interop_maturity_score, source: risk.standards_enabled },
  ];

  let primaryBlocker = 'unknown';
  let summary = '';

  if (risk.interview_count === 0) {
    primaryBlocker = 'people';
    summary = 'No stakeholder interviews conducted. People readiness cannot be assessed.';
  } else if (risk.document_count === 0) {
    primaryBlocker = 'governance';
    summary = 'No governance documents analyzed. Policy maturity unknown.';
  } else if (risk.standards_enabled < 2) {
    primaryBlocker = 'interop';
    summary = `Only ${risk.standards_enabled}/${risk.standards_total} interoperability standards enabled.`;
  } else {
    const validFactors = factors.filter(f => f.score !== null);
    if (validFactors.length > 0) {
      const weakest = validFactors.sort((a, b) => (a.score ?? 100) - (b.score ?? 100))[0];
      primaryBlocker = weakest.key;
      if (weakest.key === 'people') {
        summary = `People readiness at ${Math.round(weakest.score!)}%. Stakeholder alignment needs work.`;
      } else if (weakest.key === 'governance') {
        summary = `Governance maturity at ${Math.round(weakest.score!)}%. Documentation gaps detected.`;
      } else {
        summary = `Interoperability at ${Math.round(weakest.score!)}%. Standards adoption is limited.`;
      }
    } else {
      summary = 'Complete organizational assessments for full risk analysis.';
    }
  }

  if (risk.risk_level === 'LOW' && !summary.includes('No ')) {
    summary = 'Organization well-prepared. All readiness factors above threshold.';
  }

  return { primaryBlocker, summary };
}
